import { faArrowsLeftRight, faCheck, faMinus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { type FC, MouseEventHandler, useState } from "react";
import { useNavigate } from "react-router";
import { useApiCall } from "../api/api";
import type { Exam, ExamParticipant } from "../api/dto";
import { CheckBox } from "../components/form/CheckBox";
import { Select } from "../components/form/Select";
import { ModalFooter } from "../components/layout/ModalProvider";
import { useRefresh } from "../components/RefreshController";

export const MoveExamParticipantModal: FC<{
  exams: Exam[];
  exam: Exam & { participants: ExamParticipant[] };
}> = (props) => {
  const { moveParticipant } = useApiCall();
  const reload = useRefresh();
  const navigate = useNavigate();

  // Participant
  const [participants, setParticipants] = useState<ExamParticipant["id"][]>([]);

  // Exams
  const [exam, setExam] = useState<Exam["id"]>(props.exam.id);
  const examOptions = Object.fromEntries(
    props.exams
      .filter((e) => !e.archivedAt)
      .filter((e) => e.questionSetId === props.exam.questionSetId)
      .sort((a, b) => b.name?.localeCompare(a.name ?? "") ?? -1)
      .map((e) => [e.id, e.id === props.exam.id ? `${e.name} (huidig)` : (e.name ?? "-")]),
  );

  const confirm = async () => {
    const promises = participants.map(
      async (participant) => await moveParticipant(props.exam.id, participant, exam),
    );
    await Promise.all(promises);
    reload();
    navigate(`/exams/${exam}`);
  };

  return (
    <>
      <p>
        <strong>Let op!</strong> Dit verplaatst deze deelnemer,{" "}
        <strong>inclusief resultaten</strong> naar het gekozen examen.
      </p>
      <div className={"grid grid-cols-1 gap-3 mt-3"}>
        <label className={`flex flex-col text-sm font-medium text-brand-900 w-full max-w-md`}>
          Deelnemers ({participants.length} geselecteerd)
        </label>
        {props.exam.participants.map((p, i) => {
          return (
            <div
              key={i}
              className={`flex flex-row border border-slate-200 rounded-sm items-center ${i === 0 ? "-mt-2" : ""} hover:bg-brand-50 cursor-pointer`}
              onClick={() =>
                setParticipants((prev) =>
                  participants.includes(p.id) ? prev.filter((pp) => p.id !== pp) : [...prev, p.id],
                )
              }
            >
              <CheckBox
                state={participants.includes(p.id) ? "checked" : "unchecked"}
                onSelect={() => {
                  setParticipants((prev) => {
                    return [...prev, p.id];
                  });
                }}
                onDeselect={() => {
                  setParticipants((prev) => prev.filter((id) => id !== p.id));
                }}
              />
              <div className={"ml-2"}>{p.name}</div>
            </div>
          );
        })}
        <Select
          options={examOptions}
          value={exam}
          onChange={setExam}
          label={"Verplaatsen naar Examen"}
        />
      </div>
      <ModalFooter
        icon={faArrowsLeftRight}
        text={"Verplaatsen"}
        onSubmit={confirm}
        disabled={exam === props.exam.id || participants.length === 0}
      />
    </>
  );
};
